import { defineStore } from "pinia";
import {
  guessRegionFromTimezone,
  guessLanguageFromNavigator,
} from "../services/discoverProviders.js";
import {
  languagesForRegion,
  originalLanguagePipe,
} from "../helpers/region-languages.js";
import { normalizeLang } from "../helpers/language-preference.js";
import { sanitizePreferredPlayer } from "../helpers/preferred-player.js";
import {
  sanitizeNormalizerLevel,
  DEFAULT_NORMALIZER_LEVEL,
} from "../helpers/audio-normalizer.js";
import { apiFetch } from "../services/apiClient.js";
import { syncThemeChrome } from "../helpers/theme-chrome.js";

// Hard cap on the preferred-language list (primary + secondary).
const MAX_PREFERRED_LANGUAGES = 2;

// Coerce a preferred-language list into clean ISO 639-1 codes: normalize
// (lowercase, locale-strip, 639-2→639-1), keep only 2-letter codes, dedupe,
// cap at primary+secondary.
function sanitizePreferredLanguages(value) {
  if (!Array.isArray(value)) return [];
  const seen = new Set();
  const out = [];
  for (const entry of value) {
    const code = normalizeLang(entry);
    if (!/^[a-z]{2}$/.test(code)) continue;
    if (seen.has(code)) continue;
    seen.add(code);
    out.push(code);
    if (out.length >= MAX_PREFERRED_LANGUAGES) break;
  }
  return out;
}

// Keys that round-trip to /api/auth/preferences (per-invite, shared
// across every device on the same invite). Add a key here when its
// allowlist entry lands in server/auth/routes.js's PREFERENCE_KEYS.
const SYNCED_PREFERENCES = [
  "region",
  "regions",
  "language",
  "providerSet",
  "excludeLowResolution",
  "originalLanguagesOverride",
];

// Hard cap on the number of selected regions. Each region fans out a
// separate TMDB request per shelf, so 5 already produces ~50 parallel
// calls on first paint of /discover. Anything higher punishes the
// shared TMDB API key without giving the user meaningfully more
// content variety.
const MAX_REGIONS_LENGTH = 5;

function sanitizeRegions(value) {
  if (!Array.isArray(value)) return null;
  const seen = new Set();
  const out = [];
  for (const entry of value) {
    if (typeof entry !== "string") continue;
    const code = entry.trim().toUpperCase();
    if (!/^[A-Z]{2}$/.test(code)) continue;
    if (seen.has(code)) continue;
    seen.add(code);
    out.push(code);
    if (out.length >= MAX_REGIONS_LENGTH) break;
  }
  return out;
}

// Hard cap on the number of streaming providers we'll honor in a manual
// override. Each provider id participates in the unified /discover filter
// view and fans out TMDB calls. A tampered localStorage value of e.g.
// `providerSet=[1,2,...,10000]` would otherwise fan out tens of thousands
// of TMDB requests (self-DoS for that user, plus risk of getting the shared
// TMDB API key rate-limited for every user).
// 12 is comfortably above the curated max (7) and the UI's practical max.
const MAX_PROVIDER_SET_LENGTH = 12;

function sanitizeProviderSet(value) {
  if (value === null || value === undefined) return null;
  if (!Array.isArray(value)) return null;
  // Coerce to numbers, drop non-finite, dedupe, then cap.
  const seen = new Set();
  const out = [];
  for (const entry of value) {
    const n = Number(entry);
    if (!Number.isFinite(n)) continue;
    if (seen.has(n)) continue;
    seen.add(n);
    out.push(n);
    if (out.length >= MAX_PROVIDER_SET_LENGTH) break;
  }
  return out;
}

// Resolves once the per-invite preferences have been hydrated from the
// server (or after a safety timeout, so callers never hang on a slow or
// never-called hydrate -- e.g. on public/locked pages). Region- and
// language-driven shelves (CategoryShelf) await this before their FIRST
// fetch so they don't load with the localStorage-guessed region/language
// and then visibly clear-to-skeletons + reload + reshuffle when the
// server's real values land (the reported "shelves load then jarringly
// re-sort / rapidly moving thumbnails" jank). User-initiated filter
// changes AFTER boot still re-fetch immediately.
let _resolvePrefsReady;
const _prefsReady = new Promise((resolve) => {
  _resolvePrefsReady = resolve;
});
// Safety valve: never block shelves longer than this even if
// hydratePreferencesFromServer is never called or hangs.
const _prefsReadyTimer = setTimeout(() => {
  if (_resolvePrefsReady) {
    const r = _resolvePrefsReady;
    _resolvePrefsReady = null;
    r();
  }
}, 2500);
function markPrefsReady() {
  if (!_resolvePrefsReady) return;
  clearTimeout(_prefsReadyTimer);
  const r = _resolvePrefsReady;
  _resolvePrefsReady = null;
  r();
}

export const useSettingsStore = defineStore("settings", {
  state: () => {
    const primary = guessRegionFromTimezone();
    return {
      theme: "dark", // 'auto', 'light', 'dark'
      excludeLowResolution: true, // Exclude 720p and 480p torrents by default
      // `region` (singular) is the primary region. Kept as a top-level
      // state field so existing callsites (settings.region, badges,
      // BrowseMediaView) keep working without touching every consumer.
      // The source of truth is `regions[0]` -- setRegion + setRegions
      // keep both in sync.
      region: primary,
      // Multi-region selection (Feature 31). Always >= 1; the first
      // entry is the primary (drives badge lookup + the legacy
      // single-region settings.region).
      regions: [primary],
      language: guessLanguageFromNavigator(),
      providerSet: null,
      contentType: "all",
      // TMDB with_original_language allowlist. null = auto-derive from
      // region (e.g. region=TH yields ['th','en']); an array overrides
      // that derivation for power users.
      originalLanguagesOverride: null,
      // Feature 48: last target language chosen in the player's "Translate
      // captions" control (ISO 639-1 code, e.g. 'es'). '' = none chosen yet.
      // Local-only convenience (auto-persisted), not synced across devices.
      captionTranslateLanguage: "",
      // Feature 48: last translation MODEL chosen in the player ('' = server
      // default). Local-only, auto-persisted.
      captionTranslateModel: "",
      // CLAUDE-TODO #343: preferred audio + subtitle languages, primary first
      // ([0] = primary, [1] = secondary). Drives subtitle-list ordering and
      // audio-track pre-selection in the players (prioritize-only — does NOT
      // auto-enable subtitles). Local-only (auto-persisted); ISO 639-1 codes.
      // Seeded from the browser language so a fresh user gets sensible
      // ordering without configuring anything.
      preferredLanguages: (() => {
        const base = normalizeLang(guessLanguageFromNavigator());
        return /^[a-z]{2}$/.test(base) ? [base] : [];
      })(),
      // CLAUDE-TODO #420: when true, the player auto-enables a subtitle track on
      // load, preferring `preferredLanguages` (falls back to the first available
      // track). Off by default; local-only (auto-persisted).
      subtitlesOnByDefault: false,
      // Preferred player backing the /browse hero Play button when a title
      // has multiple sources: 'auto' | 'putio' | 'torbox' | 'plex:<serverId>'
      // (helpers/preferred-player.js vocabulary). Local-only (auto-persisted)
      // — deliberately per-device, like the player-language prefs: the living
      // room TV and a laptop can prefer different players.
      preferredPlayer: "auto",
      // Volume smoothing: run playback through a compressor so quiet dialogue
      // and loud action sit closer together. Local-only (auto-persisted) —
      // the living-room TV wants it, headphones usually don't.
      audioNormalization: false,
      audioNormalizationLevel: DEFAULT_NORMALIZER_LEVEL,
    };
  },
  persist: {
    // Migration hook: pre-Feature-31 devices have `region` in
    // localStorage but no `regions`. Backfill `regions = [region]` on
    // restore so the multi-region read paths have a valid array
    // without forcing the user to re-pick.
    afterRestore: (ctx) => {
      const store = ctx.store;
      const cleaned = sanitizeRegions(store.regions);
      if (cleaned && cleaned.length > 0) {
        store.regions = cleaned;
        store.region = cleaned[0];
        return;
      }
      const fallback =
        typeof store.region === "string" && /^[A-Z]{2}$/.test(store.region)
          ? store.region
          : guessRegionFromTimezone();
      store.regions = [fallback];
      store.region = fallback;
    },
  },
  getters: {
    // Resolved original-language allowlist for /discover + the cinematic
    // rails. Honors an explicit override; otherwise derives from region.
    // Always English-inclusive in the derived path so blockbusters keep
    // surfacing in non-Anglophone regions.
    originalLanguages(state) {
      if (
        Array.isArray(state.originalLanguagesOverride) &&
        state.originalLanguagesOverride.length > 0
      ) {
        return state.originalLanguagesOverride;
      }
      // Feature 31: union of per-region default-language lists across
      // every selected region so a user with regions=['TH','FR'] gets
      // ['th','en','fr'] (Thai dramas AND French films) instead of
      // just the primary's allowlist.
      const list = Array.isArray(state.regions)
        ? state.regions
        : [state.region];
      const seen = new Set();
      const out = [];
      for (const r of list) {
        for (const code of languagesForRegion(r)) {
          if (seen.has(code)) continue;
          seen.add(code);
          out.push(code);
        }
      }
      return out.length > 0 ? out : languagesForRegion(state.region);
    },
    // Pipe-joined shape TMDB expects for with_original_language.
    // Components/services that previously hardcoded "en|fr" now pull
    // this getter so a Thai user gets "th|en", a Japanese user gets
    // "ja|en", and so on.
    originalLanguagePipe(state) {
      if (
        Array.isArray(state.originalLanguagesOverride) &&
        state.originalLanguagesOverride.length > 0
      ) {
        return state.originalLanguagesOverride.join("|");
      }
      const list = Array.isArray(state.regions)
        ? state.regions
        : [state.region];
      const seen = new Set();
      const out = [];
      for (const r of list) {
        for (const code of languagesForRegion(r)) {
          if (seen.has(code)) continue;
          seen.add(code);
          out.push(code);
        }
      }
      return out.length > 0
        ? out.join("|")
        : originalLanguagePipe(state.region);
    },
  },
  actions: {
    setTheme(theme) {
      this.theme = theme;
      this.applyTheme();
    },

    setExcludeLowResolution(exclude) {
      this.excludeLowResolution = !!exclude;
      this.queueSyncPreferences({ excludeLowResolution: !!exclude });
    },

    setRegion(region) {
      // Single-region setter keeps the existing call surface working.
      // Collapses the multi-region selection down to [region] so the
      // user can't end up with a primary that isn't in `regions`.
      this.region = region;
      this.regions = [region];
      this.providerSet = null;
      // Region change implicitly clears the provider override too;
      // push BOTH to the server so a fresh device on the same invite
      // doesn't inherit a stale provider list pinned to the old region.
      this.queueSyncPreferences({
        region,
        regions: [region],
        providerSet: null,
      });
    },

    // Feature 48: remember the last caption-translation target language so
    // the player pre-selects it next time. Local-only (no server sync).
    setCaptionTranslateLanguage(code) {
      this.captionTranslateLanguage = typeof code === "string" ? code : "";
    },

    // Feature 48: remember the last translation model picked in the player.
    setCaptionTranslateModel(id) {
      this.captionTranslateModel = typeof id === "string" ? id : "";
    },

    // CLAUDE-TODO #343: set the preferred audio+subtitle languages. Accepts a
    // [primary, secondary] array (codes or names normalize to ISO 639-1);
    // sanitizes + caps at 2. Local-only.
    setPreferredLanguages(value) {
      this.preferredLanguages = sanitizePreferredLanguages(value);
    },

    setAudioNormalization(value) {
      this.audioNormalization = !!value;
    },

    setAudioNormalizationLevel(value) {
      this.audioNormalizationLevel = sanitizeNormalizerLevel(value);
    },

    // Preferred player for the /browse hero Play button. Canonicalizes to
    // the preferred-player.js vocabulary (junk degrades to 'auto'). Local-only.
    setPreferredPlayer(value) {
      this.preferredPlayer = sanitizePreferredPlayer(value);
    },

    // Multi-region setter (Feature 31). Accepts the full array the user
    // has selected in the multi-region picker. Validates (ISO-3166-1
    // alpha-2, uppercase, max 5, deduped); falls back to the current
    // primary when the caller hands us an empty/all-invalid list.
    setRegions(value) {
      const cleaned = sanitizeRegions(value);
      const next =
        cleaned && cleaned.length > 0
          ? cleaned
          : [this.region || guessRegionFromTimezone()];
      this.regions = next;
      this.region = next[0];
      this.providerSet = null;
      this.queueSyncPreferences({
        region: next[0],
        regions: next.slice(),
        providerSet: null,
      });
    },

    setLanguage(lang) {
      this.language = lang;
      this.queueSyncPreferences({ language: lang });
    },

    setProviderSet(arrayOrNull) {
      this.providerSet = sanitizeProviderSet(arrayOrNull);
      // null on the wire un-pins the override server-side; an array
      // pins it. Either way the next device on this invite hydrates
      // to the same view.
      this.queueSyncPreferences({ providerSet: this.providerSet });
    },

    setContentType(type) {
      this.contentType = type;
    },

    // Explicit override for the original-language allowlist. Pass null
    // (or an empty array) to revert to region-derived defaults.
    setOriginalLanguagesOverride(arrayOrNull) {
      if (!Array.isArray(arrayOrNull) || arrayOrNull.length === 0) {
        this.originalLanguagesOverride = null;
        this.queueSyncPreferences({ originalLanguagesOverride: null });
        return;
      }
      // Keep only short ISO-639-1-shaped strings (2-3 chars, ASCII).
      const cleaned = [];
      const seen = new Set();
      for (const entry of arrayOrNull) {
        if (typeof entry !== "string") continue;
        const code = entry.trim().toLowerCase();
        if (!/^[a-z]{2,3}$/.test(code)) continue;
        if (seen.has(code)) continue;
        seen.add(code);
        cleaned.push(code);
        if (cleaned.length >= 8) break;
      }
      this.originalLanguagesOverride = cleaned.length > 0 ? cleaned : null;
      this.queueSyncPreferences({
        originalLanguagesOverride: this.originalLanguagesOverride,
      });
    },

    applyTheme() {
      const html = document.documentElement;

      if (html.classList.contains("tv-mode") || this.theme === "dark") {
        html.classList.add("dark");
      } else if (this.theme === "light") {
        html.classList.remove("dark");
      } else {
        // Auto mode - use system preference
        if (window.matchMedia("(prefers-color-scheme: dark)").matches) {
          html.classList.add("dark");
        } else {
          html.classList.remove("dark");
        }
      }
      syncThemeChrome();
    },

    initializeTheme() {
      // Apply theme on initialization
      this.applyTheme();

      // Listen for system theme changes when in auto mode
      const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
      mediaQuery.addEventListener("change", () => {
        if (this.theme === "auto") {
          this.applyTheme();
        }
      });
    },

    // ---- Per-invite preference sync (DB v17) ----
    // Hydrate the synced subset (region, language, …) from the server
    // so a fresh device on the same invite picks up the user's
    // preferences instead of guessing from timezone/navigator and
    // making them re-pick. Failure is silent — local defaults stay
    // in place. Called from App.vue once auth is unlocked.
    async hydratePreferencesFromServer() {
      try {
        const body = await apiFetch("/api/auth/preferences");
        const prefs =
          body && typeof body === "object" ? body.preferences : null;
        if (!prefs || typeof prefs !== "object") return;
        // Only assign keys we actually expose so a server with future
        // keys we don't know about doesn't pollute state.
        for (const key of SYNCED_PREFERENCES) {
          if (Object.prototype.hasOwnProperty.call(prefs, key)) {
            const value = prefs[key];
            if (value === null || value === undefined) continue;
            this[key] = value;
          }
        }
        // Feature 31: reconcile region <-> regions after a multi-key
        // hydrate. Server may return only `region` (older invite), only
        // `regions` (newer), or both -- always end with both populated
        // and `regions[0] === region` so downstream getters don't see
        // drift between the two.
        const cleanedRegions = sanitizeRegions(this.regions);
        if (cleanedRegions && cleanedRegions.length > 0) {
          this.regions = cleanedRegions;
          this.region = cleanedRegions[0];
        } else if (
          typeof this.region === "string" &&
          /^[A-Z]{2}$/.test(this.region)
        ) {
          this.regions = [this.region];
        }
      } catch {
        // Locked / 404 / network error — leave the local defaults in
        // place. The next setRegion/setLanguage call will push to
        // the server when it's reachable again.
      } finally {
        // Unblock region/language-driven shelves whether or not the
        // hydrate succeeded — they should render with the best values
        // we have rather than wait on a failed request.
        markPrefsReady();
      }
    },

    // Promise that resolves once preferences have hydrated from the
    // server (or after the safety timeout). CategoryShelf awaits this
    // before its first fetch to avoid the load-with-guess-then-reload
    // jank. Idempotent and safe to await many times.
    whenPrefsReady() {
      return _prefsReady;
    },

    // Debounced PATCH to /api/auth/preferences. The store calls this
    // from setRegion/setLanguage; multiple rapid changes coalesce
    // into a single request.
    queueSyncPreferences(patch) {
      if (!patch || typeof patch !== "object") return;
      // Lazily allocate the debounce scratch state. These live on the
      // store instance but outside `state()` so they don't get
      // persisted to localStorage and don't trigger reactive updates.
      if (!this._pendingPrefSync) this._pendingPrefSync = {};
      Object.assign(this._pendingPrefSync, patch);
      if (this._prefSyncTimer) clearTimeout(this._prefSyncTimer);
      this._prefSyncTimer = setTimeout(() => {
        const toSend = this._pendingPrefSync || {};
        this._pendingPrefSync = {};
        this._prefSyncTimer = null;
        // Filter to keys the server is known to accept so we don't
        // send junk that gets dropped silently.
        const filtered = {};
        for (const key of SYNCED_PREFERENCES) {
          if (Object.prototype.hasOwnProperty.call(toSend, key)) {
            filtered[key] = toSend[key];
          }
        }
        if (Object.keys(filtered).length === 0) return;
        apiFetch("/api/auth/preferences", {
          method: "PATCH",
          json: filtered,
        }).catch(() => {
          // Best-effort. Local state still updated; the next sync
          // (or hydrate on another device) will pick up the change
          // once the server is reachable.
        });
      }, 400);
    },
  },
});
